// pages/Users/ListUsers.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import {
  Box,
  Typography,
  Button,
  Alert,
  Modal,
  CircularProgress,
  Paper,
  Chip,
  useTheme,
} from "@mui/material";
import EditUser from "./EditUser";

export default function ListUsers({ refresh, onRefresh }) {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [refresh]);


  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/users`);
      if (response.data.success) {
        const data =
          typeof response.data.data === "string"
            ? JSON.parse(response.data.data)
            : response.data.data;
        setUsers(data.users || []);
        setError(null);
      } else {
        setError(response.data.message || "Failed to fetch users");
      }
    } catch (err) {
      setError("Error fetching users: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this user?")) return;

    try {
      const response = await axiosInstance.delete(`/api/users/${userId}`);
      if (response.data.success) {
        setUsers(users.filter((u) => u.user_id !== userId));
        setSuccess("User removed successfully");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.data.message || "Failed to remove user");
      }
    } catch (err) {
      setError("Error removing user: " + (err.response?.data?.message || err.message));
    }
  };

  const handleEdit = (user) => {
    setCurrentUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentUser(null);
  };

  const updateModal = () => {
    fetchUsers();
    closeModal();
    if (onRefresh) onRefresh();
  };

  // ✅ Custom MUI-based DataTable styling for dark mode
const customDataTableStyles = {
  table: {
    style: {
      backgroundColor: theme.palette.background.paper,
    },
  },
  headRow: {
    style: {
      backgroundColor:
        theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${
        theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
      }`,
    },
  },
  headCells: {
    style: {
      fontWeight: 600,
      fontSize: "14px",
      color: theme.palette.text.primary,
    },
  },
  rows: {
    style: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor:
          theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
      },
      "&.rdt_Row:hover": {
        backgroundColor:
          theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
      },
      "&.rdt_Row--selected": {
        backgroundColor:
          theme.palette.mode === "dark" ? "#131b2b !important" : "#e2e8f0",
      },
    },
  },
  pagination: {
    style: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderTop: `1px solid ${
        theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
      }`,
    },
  },
  expanderRow: {
    style: {
      backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
    },
  },
};

  const columns = [
    {
      name: "Name",
      width: '120px', 
      selector: (row) =>
        row.profile
          ? `${row.profile.fname || ""} ${row.profile.lname || ""}`.trim()
          : "N/A",
      sortable: true,
    },
    { name: "Email", selector: (row) => row.user_email, sortable: true },
    {
      name: "Role",
      width: '250px', 
      cell: (row) => {
        return (
          <Box display="flex" gap={0.5}>
            <Chip
              label={row.role_in_team.charAt(0).toUpperCase() + row.role_in_team.slice(1)}
              color={row.is_system_role_in_team ? "success" : "info"}
              size="small"
            />
            <Chip
              label={row.is_system_role_in_team ? "Global" : "Custom"}
              variant="outlined"
              color={row.is_system_role_in_team ? "success" : "info"}
              size="small"
            />
          </Box>
        );
      },
    },
    {
      name: "Status",
      width: '150px', 
      cell: (row) => (
        <Chip
          label={row.verified ? "Verified" : "Pending"}
          color={row.verified ? "success" : "warning"}
          size="small"
        />
      ),
      sortable: true,
    },
    {
      name: "Joined",
      width: '150px', 
      selector: (row) =>
        row.created_on ? new Date(row.created_on).toLocaleDateString() : "N/A",
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => handleEdit(row)}
          >
            <i className="fa fa-pencil" />
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleDelete(row.user_id)}
          >
            <i className="fa fa-trash-o" />
          </Button>
        </Box>
      ),
      ignoreRowClick: true,
    },
  ];

  const muiModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: "80%", md: "600px" },
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    outline: "none",
    p: 0,
  };

  return (
    <Paper
      sx={{
        p: 3,
        mt: 3,
        borderRadius: 3,
        boxShadow: theme.palette.mode === "dark" ? 5 : 2,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" color="text.primary">
          Team Members
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={() => {
            fetchUsers();
          }}
          disabled={loading}
        >
          <i className="fa fa-refresh" /> Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <i className="fa fa-info-circle" />{" "}
        <strong>Role Types:</strong>{" "}
        <Chip label="Global" color="success" size="small" /> shared across all
        teams | <Chip label="Custom" color="info" size="small" /> specific to
        your team
      </Alert>

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
          <CircularProgress size={30} />
          <Typography mt={2}>Loading users...</Typography>
        </Box>
      ) : users.length === 0 ? (
        <Box textAlign="center" py={4}>
          <i className="fa fa-users fa-3x text-muted" />
          <Typography mt={2} color="text.primary">
            No team members found
          </Typography>
        </Box>
      ) : (
        <DataTable
          columns={columns}
          data={users}
          pagination
          responsive
          highlightOnHover
          pointerOnHover
          theme={theme.palette.mode === "dark" ? "dark" : "default"}
          customStyles={customDataTableStyles}
          expandableRows
          expandableRowsComponent={({ data }) => (
            <Box p={2}>
              <Typography variant="body2" color="text.primary">
                <strong>Email:</strong> {data.user_email}
                <br />
                <strong>Joined:</strong>{" "}
                {data.created_on
                  ? new Date(data.created_on).toLocaleString()
                  : "N/A"}
              </Typography>
            </Box>
          )}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <i className="fa fa-exclamation-triangle" /> {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <i className="fa fa-check" /> {success}
        </Alert>
      )}

      <Modal open={isModalOpen} onClose={closeModal} aria-labelledby="edit-user-modal">
        <Box sx={muiModalStyle}>
          <EditUser user={currentUser} onClose={closeModal} onUpdate={updateModal} />
        </Box>
      </Modal>
    </Paper>
  );
}
